import React, { useEffect, useState } from "react";
import { Button, Form, Input, message } from "antd";
import { API_ENDPOINT_ONBOARDING_COMPANY_INFO_UPDATE } from "app/shared/MasterLayout/masterlayout.constant";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { apiPost } from "app/services/apiServices";
import { userInfo, operationTypeState } from "app/config/States/users";
import ShieldIcon from "../../../../assets/images/shield-tick-safe (1).png";
import "./CompanyDetails.scss";

interface StepOneProps {
  onNext: () => void;
}

const CompanyDetails: React.FC<StepOneProps> = ({ onNext }) => {
  const [isLoading, setLoading] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);

  const [userDetails, setUserDetails] = useRecoilState<any>(userInfo);
  const [, setOperationType] = useRecoilState(operationTypeState);

  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Prefill form
  useEffect(() => {
    form.setFieldsValue({
      name: userDetails?.workspaceInfo?.company || "",
      website: userDetails?.workspaceInfo?.website || "",
      pan: userDetails?.workspaceInfo?.pan || "",
    });
  }, [userDetails, form]);

  useEffect(() => {
    const values = form.getFieldsValue();
    const { name, website, pan } = values;
    const isFilled = name?.trim() && website?.trim() && pan?.trim();
    setButtonDisabled(!isFilled);
  }, [userDetails]);

  

  const isValidPAN = (pan: string) => {
    const panPattern = /^(?:[0-9A-Z]{10}|[0-9A-Z]{13})$/;
    return panPattern.test(pan);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!isValidPAN(values.pan.toUpperCase())) {
        message.error("Please enter a valid PAN number");
        return;
      }

      setLoading(true);

      const operationType =
        userDetails?.workspaceList?.length > 0 ? "UPDATE" : "CREATE";

      const payload = {
        oldPan: userDetails?.workspaceInfo?.pan || "",
        oldcompany: userDetails?.workspaceInfo?.company || "",
        company: values.name,
        pan: values.pan.toUpperCase(),
        website: values.website,
        type: "PARTNER",
        entity_type: "CORPORATE",
        operationType,
        id: userDetails?.workspaceInfo?.id,
      };

      const response = await apiPost(
        API_ENDPOINT_ONBOARDING_COMPANY_INFO_UPDATE,
        payload
      );

      if (response.status) {
        const newWorkspace = response.data;

        const newPanObj = {
          pan: newWorkspace.pan,
          entity_id: newWorkspace.id,
          entity_name: newWorkspace.company,
          last_updated: newWorkspace.last_updated,
          box_folder_id: newWorkspace.box_folder_id,
        };

        setUserDetails((prev: any) => ({
          ...prev,
          pans: prev.pans?.some((p: any) => p.pan === newPanObj.pan)
            ? prev.pans.map((p: any) =>
                p.pan === newPanObj.pan ? { ...p, ...newPanObj } : p
              )
            : [...(prev.pans || []), newPanObj],

          workspaceList: prev.workspaceList?.some(
            (w: any) => w.id === newWorkspace.id
          )
            ? prev.workspaceList.map((w: any) =>
                w.id === newWorkspace.id ? { ...w, ...newWorkspace } : w
              )
            : [...(prev.workspaceList || []), newWorkspace],

          workspaceInfo: { ...prev.workspaceInfo, ...newWorkspace },
        }));

        message.success("Company details updated");
        onNext();
      } else {
        if (response?.error_code === "404") {
          message.error(
            `Workspace name already exists. Please use a unique name`
          );
        } else if (response?.error_code === "402") {
          message.error(response?.error);
        } else {
          message.error("Could not update company details");
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="company-details-form">
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_, allValues) => {
          const { name, website, pan } = allValues;
          const isFilled =
            name?.trim() && website?.trim() && pan?.trim();
          setButtonDisabled(!isFilled);
        }}
      >
        <div className="form-header">Enter Company Details</div>

        <Form.Item
          label="Company name"
          name="name"
          rules={[{ required: true, message: "Please input company name!" }]}
        >
          <Input placeholder="Enter company name" />
        </Form.Item>

        <Form.Item
          label="Company Website"
          name="website"
          rules={[
            { required: true, message: "Please input company website!" },
          ]}
        >
          <Input placeholder="Enter company website" />
        </Form.Item>

        <Form.Item
          label="PAN Number"
          name="pan"
          rules={[
            { required: true, message: "Please input PAN number!" },
            {
              pattern: /^(?:[0-9a-zA-Z]{10}|[0-9a-zA-Z]{13})$/,
              message: "Enter valid PAN",
            },
          ]}
        >
          <Input placeholder="Enter PAN Number" />
        </Form.Item>

        <div className="info-text">
          <img src={ShieldIcon} alt="shield icon" />
          <p>No spam, your information is safe with us!</p>
        </div>

        <Button
          type="primary"
          className="tax-manager-submit-button"
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          loading={isLoading}
          style={{
            backgroundColor: isButtonDisabled ? "#A8A8A8" : "#74003B",
            borderColor: isButtonDisabled ? "#A8A8A8" : "#74003B",
          }}
        >
          Next
        </Button>
      </Form>
    </div>
  );
};

export default CompanyDetails;